// Client cancellation is not server authorization. Each context visit owns its
// pending work; returning to the same domain does not revive an earlier visit.
type Identity = { tenant_id: string; domain_id: string };
let generation = 0;
let boundary = "initial";
let selected: Identity | null = null;
let configured = false;
const cleanups = new Set<() => void>();
export class WorkspaceChangedError extends Error {
  constructor() { super("Workspace changed; retry in the current context"); this.name = "AbortError"; }
}
export function changeWorkspaceBoundary(key: string, identity: Identity | null) {
  configured = true;
  if (key === boundary) return;
  generation++; boundary = key; selected = identity ? { ...identity } : null;
  for (const cleanup of [...cleanups]) cleanup();
  cleanups.clear();
}
export function invalidateWorkspaceBoundary() {
  changeWorkspaceBoundary(`invalid:${generation + 1}`, null);
}
export function captureWorkspaceRequest(signal?: AbortSignal | null) {
  const version = generation, controller = new AbortController();
  const abort = () => controller.abort();
  const release = () => { cleanups.delete(abort); signal?.removeEventListener("abort", abort); };
  cleanups.add(abort);
  if (signal?.aborted) abort(); else signal?.addEventListener("abort", abort, { once: true });
  return { signal: controller.signal, release, assertCurrent() {
    if (version !== generation || controller.signal.aborted) throw new WorkspaceChangedError();
  } };
}
export function payloadHeaders(path: string, original?: HeadersInit): Headers {
  const headers = new Headers(original);
  if (!/\/pipeline\//.test(path) || !configured) return headers;
  if (!selected) throw new WorkspaceChangedError();
  for (const [key, value] of [["X-Tenant-ID", selected.tenant_id], ["X-Domain-ID", selected.domain_id]]) {
    if (headers.has(key) && headers.get(key) !== value) throw new WorkspaceChangedError();
    headers.set(key, value);
  }
  return headers;
}
// Guard response arrival AND body consumption; cancelling fetch alone does not
// protect an already-resolved response or a transport that ignores AbortSignal.
export async function workspaceFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const request = captureWorkspaceRequest(init.signal);
  try {
    const response = await fetch(input, { ...init, headers: payloadHeaders(input, init.headers), signal: request.signal, cache: "no-store" });
    request.assertCurrent();
    if (!response.ok) request.release();
    return new Proxy(response, { get(target, property) {
      if (["json", "blob", "text", "arrayBuffer", "formData"].includes(String(property))) return async () => {
        try { const result = await (target[property as "json"] as () => Promise<unknown>).call(target); request.assertCurrent(); return result; }
        finally { request.release(); }
      };
      const value = Reflect.get(target, property, target);
      return typeof value === "function" ? value.bind(target) : value;
    } });
  } catch (error) { request.release(); throw error; }
}
export function workspaceStream(url: string, onData: (event: MessageEvent) => void, onError?: () => void): () => void {
  const request = captureWorkspaceRequest();
  const stream = new EventSource(url, { withCredentials: true });
  const close = () => { stream.close(); request.release(); request.signal.removeEventListener("abort", close); };
  request.signal.addEventListener("abort", close, { once: true });
  stream.onmessage = event => { try { request.assertCurrent(); onData(event); } catch {} };
  stream.onerror = () => { try { request.assertCurrent(); onError?.(); } catch {} };
  return close;
}
